import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { domainAPI, statsAPI, featuredAPI } from '../lib/api';
import DomainCard from '../components/DomainCard';
import { Search, ArrowRight, Zap, Shield, TrendingUp, Globe, Gavel, Leaf, BarChart3, Handshake, DollarSign, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useSEO } from '../lib/useSEO';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_2d045789-83c2-4d79-aa2c-4eeefb9b25ce/artifacts/n3gtt7ti_X%20Logo-01.png";

export default function HomePage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [featuredAuctions, setFeaturedAuctions] = useState([]);
  const [stats, setStats] = useState({});
  const [search, setSearch] = useState('');

  useSEO({ title: 'XDomainX - Premium Domain Marketplace', description: 'Buy and sell premium domain names. Live auctions, domain financing, and expert valuations at XDomainX.com.', keywords: 'domain names, buy domains, sell domains, domain auction, premium domains' });

  useEffect(() => {
    domainAPI.featured().then(r => setFeatured(r.data.domains || [])).catch(() => {});
    featuredAPI.auctions(8).then(r => setFeaturedAuctions(r.data.auctions || [])).catch(() => {});
    statsAPI.public().then(r => setStats(r.data)).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/domains?search=${encodeURIComponent(search)}`);
  };

  const services = [
    { icon: Globe, title: t('nav.domains'), desc: 'Buy premium domains instantly', path: '/domains' },
    { icon: Gavel, title: t('nav.auctions'), desc: 'Bid on exclusive domain auctions', path: '/auctions' },
    { icon: BarChart3, title: t('services.valuation'), desc: 'Get instant domain appraisals', path: '/valuation' },
    { icon: Leaf, title: t('services.garden'), desc: 'Domain rescue & adoption program', path: '/domain-garden' },
    { icon: Handshake, title: t('services.brokerage'), desc: 'Expert domain negotiations', path: '/brokerage' },
    { icon: TrendingUp, title: t('services.sell'), desc: 'List and sell your domains', path: '/sell-domain' },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="hero-bg relative noise-overlay" data-testid="hero-section">
        <div className="relative z-10 px-6 md:px-12 max-w-7xl mx-auto pt-20 md:pt-32 pb-24 md:pb-40">
          <div className="max-w-4xl">
            <img src={LOGO_URL} alt="XDomainX" className="h-20 md:h-28 w-auto mb-8 animate-fade-up opacity-0" style={{ animationFillMode: 'forwards' }} />
            <h1 className="text-5xl sm:text-6xl lg:text-8xl font-black tracking-tighter leading-none font-['Outfit'] animate-fade-up opacity-0 stagger-1" style={{ animationFillMode: 'forwards' }}>
              {t('hero.title')}<br />
              <span className="gradient-text">{t('hero.titleAccent')}</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed animate-fade-up opacity-0 stagger-2" style={{ animationFillMode: 'forwards' }}>
              {t('hero.subtitle')}
            </p>

            {/* Search */}
            <form onSubmit={handleSearch} className="mt-10 flex gap-3 max-w-xl animate-fade-up opacity-0 stagger-3" style={{ animationFillMode: 'forwards' }}>
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-600" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('hero.search')}
                  className="pl-10 h-12 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm text-base" data-testid="hero-search-input" />
              </div>
              <Button type="submit" className="h-12 px-6 bg-[#00E5FF] text-black hover:bg-white font-bold uppercase tracking-wider rounded-sm shadow-[0_0_20px_rgba(0,229,255,0.3)] hover:shadow-[0_0_30px_rgba(0,229,255,0.6)]" data-testid="hero-search-btn">
                {t('common.search')}
              </Button>
            </form>

            {/* CTAs */}
            <div className="mt-8 flex flex-wrap gap-4 animate-fade-up opacity-0 stagger-4" style={{ animationFillMode: 'forwards' }}>
              <Link to="/domains">
                <Button className="bg-[#00E5FF] text-black hover:bg-white font-bold uppercase tracking-wider rounded-sm h-11 px-8 shadow-[0_0_20px_rgba(0,229,255,0.3)]" data-testid="explore-domains-btn">
                  {t('hero.cta')} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/auctions">
                <Button variant="outline" className="border-zinc-800 hover:border-[#00E5FF] text-white hover:text-[#00E5FF] font-bold uppercase tracking-wider rounded-sm h-11 px-8" data-testid="live-auctions-btn">
                  {t('hero.ctaAuction')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-white/5 bg-[#050505]" data-testid="stats-section">
        <div className="px-6 md:px-12 max-w-7xl mx-auto py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { label: 'Active Domains', value: stats.total_domains || 0 },
              { label: 'Live Auctions', value: stats.total_auctions || 0 },
              { label: 'Registered Users', value: stats.total_users || 0 },
              { label: 'Transactions', value: stats.total_transactions || 0 },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-[#00E5FF] font-['Outfit']">{s.value.toLocaleString()}+</div>
                <div className="text-xs text-zinc-600 uppercase tracking-widest mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Domains */}
      {featured.length > 0 && (
        <section className="py-24 md:py-32" data-testid="featured-section">
          <div className="px-6 md:px-12 max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-12">
              <div>
                <h2 className="text-xs font-bold text-[#00E5FF] uppercase tracking-widest mb-2">{t('domains.featured')}</h2>
                <p className="text-3xl md:text-4xl font-bold text-white font-['Outfit'] tracking-tight">Premium Picks</p>
              </div>
              <Link to="/domains" className="hidden md:flex items-center gap-2 text-sm text-zinc-400 hover:text-[#00E5FF] transition-colors uppercase tracking-wider">
                {t('domains.all')} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {featured.slice(0, 8).map((d, i) => (
                <div key={d.id} className="animate-fade-up opacity-0" style={{ animationDelay: `${i * 0.08}s`, animationFillMode: 'forwards' }}>
                  <DomainCard domain={d} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Auctions Carousel */}
      {featuredAuctions.length > 0 && (
        <section className="py-16 md:py-24 bg-[#050505] border-y border-white/5" data-testid="featured-auctions-section">
          <div className="px-6 md:px-12 max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-10">
              <div>
                <h2 className="text-xs font-bold text-[#FF6B9D] uppercase tracking-widest mb-2">Live Now</h2>
                <p className="text-3xl md:text-4xl font-bold text-white font-['Outfit'] tracking-tight">Featured Auctions</p>
              </div>
              <Link to="/auctions" className="hidden md:flex items-center gap-2 text-sm text-zinc-400 hover:text-[#FF6B9D] transition-colors uppercase tracking-wider">
                All Auctions <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
              {featuredAuctions.slice(0, 8).map((a, i) => {
                const timeLeft = a.auction_end ? Math.max(0, Math.floor((new Date(a.auction_end) - new Date()) / 86400000)) : 0;
                return (
                  <Link key={a.id} to={`/domains/${a.id}`}
                    className="shrink-0 w-72 p-5 bg-[#0A0A0A] border border-white/5 hover:border-[#FF6B9D]/30 rounded-sm transition-all group"
                    data-testid={`featured-auction-${i}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[10px] font-bold text-[#FF6B9D] uppercase tracking-wider bg-[#FF6B9D]/10 px-2 py-0.5 rounded-sm">Live Auction</span>
                      <span className="flex items-center gap-1 text-[10px] text-zinc-500"><Clock className="h-3 w-3" />{timeLeft}d left</span>
                    </div>
                    <h3 className="text-lg font-bold text-white font-['Outfit'] mb-1 group-hover:text-[#00E5FF] transition-colors">
                      {a.name}<span className="text-[#00E5FF]">{a.tld}</span>
                    </h3>
                    <div className="flex justify-between items-end mt-4">
                      <div>
                        <div className="text-[10px] text-zinc-600 uppercase">Current Bid</div>
                        <div className="text-xl font-bold text-[#00E5FF] font-['Outfit']">${(a.current_bid || a.price || 0).toLocaleString()}</div>
                      </div>
                      <div className="text-xs text-zinc-500">{a.bid_count || 0} bids</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Services Grid */}
      <section className="py-24 md:py-32 bg-[#050505] border-y border-white/5" data-testid="services-section">
        <div className="px-6 md:px-12 max-w-7xl mx-auto">
          <div className="mb-12">
            <h2 className="text-xs font-bold text-[#00E5FF] uppercase tracking-widest mb-2">{t('nav.services')}</h2>
            <p className="text-3xl md:text-4xl font-bold text-white font-['Outfit'] tracking-tight">Everything Domain</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((svc, i) => (
              <Link key={i} to={svc.path}
                className="block p-6 bg-[#0A0A0A] border border-white/5 hover:border-[#00E5FF]/30 rounded-sm transition-all duration-300 group" data-testid={`service-card-${i}`}>
                <svc.icon className="h-8 w-8 text-[#00E5FF] mb-4 group-hover:drop-shadow-[0_0_8px_rgba(0,229,255,0.5)] transition-all" />
                <h3 className="text-lg font-semibold text-white mb-1 font-['Outfit']">{svc.title}</h3>
                <p className="text-sm text-zinc-500">{svc.desc}</p>
                <div className="mt-4 flex items-center gap-1 text-xs text-[#00E5FF] opacity-0 group-hover:opacity-100 transition-opacity uppercase tracking-wider">
                  Learn more <ArrowRight className="h-3 w-3" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 md:py-32" data-testid="how-it-works-section">
        <div className="px-6 md:px-12 max-w-7xl mx-auto">
          <div className="mb-16 text-center">
            <h2 className="text-xs font-bold text-[#00E5FF] uppercase tracking-widest mb-2">How It Works</h2>
            <p className="text-3xl md:text-4xl font-bold text-white font-['Outfit'] tracking-tight">Three Simple Steps</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', icon: Search, title: 'Find Your Domain', desc: 'Browse our curated marketplace or search for specific domains. Filter by TLD, price, and category.' },
              { step: '02', icon: Zap, title: 'Secure It', desc: 'Buy instantly at fixed price or place competitive bids in live auctions. All transactions are protected.' },
              { step: '03', icon: Shield, title: 'Transfer & Own', desc: 'Secure escrow-based transfer ensures safe domain handover. You own it within 24-48 hours.' },
            ].map((item, i) => (
              <div key={i} className="relative p-8 bg-[#0A0A0A] border border-white/5 rounded-sm">
                <div className="text-6xl font-black text-white/[0.03] absolute top-4 right-4 font-['Outfit']">{item.step}</div>
                <item.icon className="h-10 w-10 text-[#00E5FF] mb-6" />
                <h3 className="text-xl font-bold text-white mb-3 font-['Outfit']">{item.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Services CTA */}
      <section className="py-16 md:py-20 border-t border-white/5" data-testid="partner-section">
        <div className="px-6 md:px-12 max-w-7xl mx-auto">
          <div className="mb-10 text-center">
            <h2 className="text-xs font-bold text-[#00E5FF] uppercase tracking-widest mb-2">Complete Solutions</h2>
            <p className="text-3xl md:text-4xl font-bold text-white font-['Outfit'] tracking-tight">Need More Than a Domain?</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { title: 'Need Hosting?', desc: 'Reliable, fast web hosting for your new domain. Enterprise-grade servers at competitive prices.', href: 'https://markedhost.com/', cta: 'Get Hosting', color: '#00E5FF', tid: 'partner-hosting' },
              { title: 'Need Design?', desc: 'Professional logo design and brand identity packages. Stand out from day one.', href: 'https://a-logo.com/', cta: 'Get Design', color: '#FF6B9D', tid: 'partner-design' },
              { title: 'Need Website?', desc: 'Full-service web development from landing pages to enterprise platforms.', href: 'https://webidea.co/', cta: 'Get Website', color: '#00FF94', tid: 'partner-website' },
            ].map((partner, i) => (
              <a key={i} href={partner.href} target="_blank" rel="noopener noreferrer"
                className="block p-6 bg-[#0A0A0A] border border-white/5 hover:border-white/15 rounded-sm transition-all group" data-testid={partner.tid}>
                <h3 className="text-xl font-bold text-white mb-2 font-['Outfit'] group-hover:text-[#00E5FF] transition-colors">{partner.title}</h3>
                <p className="text-sm text-zinc-500 mb-4">{partner.desc}</p>
                <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#00E5FF] group-hover:gap-3 transition-all">
                  {partner.cta} <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#050505] border-t border-white/5" data-testid="cta-section">
        <div className="px-6 md:px-12 max-w-7xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white font-['Outfit'] tracking-tight mb-4">Ready to Trade?</h2>
          <p className="text-zinc-500 mb-8 max-w-lg mx-auto">Join the fastest-growing domain marketplace. Buy, sell, or auction premium domains today.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button className="bg-[#00E5FF] text-black hover:bg-white font-bold uppercase tracking-wider rounded-sm h-12 px-10 text-base shadow-[0_0_20px_rgba(0,229,255,0.3)]" data-testid="cta-register-btn">
                Get Started Free
              </Button>
            </Link>
            <Link to="/domains">
              <Button variant="outline" className="border-zinc-800 hover:border-[#00E5FF] text-white hover:text-[#00E5FF] font-bold uppercase tracking-wider rounded-sm h-12 px-10 text-base" data-testid="cta-browse-btn">
                Browse Domains
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
